import { formatDurationEstimate } from './processing-status-badge';
import { Interval } from '../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';

interface RunApprovalPopoverContentProps {
  credits: number;
  durationEstimate?: Interval | null;
  isSubmittingApproval: boolean;
  onApprove: () => void;
  onDeny: () => void;
  runType?: string;
  subsetName?: string;
  requiresAdminApproval?: boolean;
  canApprove?: boolean;
}

export function RunApprovalPopoverContent({
  credits,
  durationEstimate,
  isSubmittingApproval,
  onApprove,
  onDeny,
  runType,
  subsetName,
  requiresAdminApproval = false,
  canApprove = true,
}: RunApprovalPopoverContentProps) {
  const approvalTitle = requiresAdminApproval
    ? `Admin Approval Required${runType ? `: ${runType}` : ''}`
    : `Run Approval Required${runType ? `: ${runType}` : ''}`;

  const approvalSubtitle = subsetName
    ? `Recontextualising facts for ${subsetName}`
    : requiresAdminApproval
      ? 'This run exceeds the admin approval threshold and requires approval from an admin'
      : 'Review the details before proceeding';

  return (
    <>
      <div className="border-b border-gray-100 bg-gradient-to-r from-amber-50/50 to-amber-50/30 px-3 py-2">
        <div className="flex items-center gap-2">
          <div>
            <h4 className="text-xs font-semibold text-gray-900">{approvalTitle}</h4>
            <p className="text-[11px] text-gray-600">{approvalSubtitle}</p>
          </div>
        </div>
      </div>
      <div className="px-3 py-2">
        <div className="mb-2 flex flex-col gap-2">
          <Badge className="border border-gray-200 bg-gray-50 text-gray-700">
            <Icon className="lucide--file-text text-blue-700" />
            <p className="text-xs text-gray-600">
              Credits required: <strong> {credits.toLocaleString?.() ?? credits}</strong>
            </p>
          </Badge>

          {durationEstimate && (
            <Badge className="border border-gray-200 bg-gray-50 text-gray-700">
              <Icon className="lucide--clock text-purple-700" />

              <p className="text-xs text-gray-600">
                Estimated time: <strong>{formatDurationEstimate(durationEstimate)}</strong>
              </p>
            </Badge>
          )}
        </div>
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-2.5">
          {!canApprove && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <Icon className="lucide--info mr-1 inline-block h-3 w-3" />
              You don't have permission to approve this run
            </span>
          )}
          {isSubmittingApproval ? (
            <div className="flex items-center gap-2">
              <Icon className="lucide--loader-2 h-4 w-4 animate-spin text-blue-600 duration-700" />
              <p className="text-xs text-gray-600">Submitting approval...</p>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="primary"
                className="flex-1"
                onClick={onApprove}
                disabled={isSubmittingApproval || !canApprove}
              >
                <Icon className="lucide--check" />
                Continue
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                onClick={onDeny}
                disabled={isSubmittingApproval || !canApprove}
              >
                <Icon className="lucide--x" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
